// Auth API: Google OAuth flow, session, profile/me endpoints, auth cache helpers.
import { apiCall, buildIdentityHeaders, getAPIRoot } from "./core";
import { isNative } from "../../lib/nativeBridge";

const AUTH_CACHE_KEY = "fs_auth_cache_v1";

export const getCachedAuthUser = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

export const getCachedAccessToken = () => {
  try {
    return getCachedAuthUser()?.access_token || null;
  } catch (_) {
    return null;
  }
};

export const authAPI = {
  startGoogleAuth: ({ next = null } = {}) => {
    const base = getAPIRoot();
    const url = new URL(`${base}/auth/google/start`, window.location.origin);
    if (next) {
      url.searchParams.set("next", next);
    }
    // Native shell: request the bearer-token handoff (code forwarded to
    // `stagster://auth` instead of a cookie). Web flow unchanged.
    if (isNative()) {
      url.searchParams.set("platform", "native");
      url.searchParams.set("scheme", "stagster");
    }
    window.location.href = url.toString();
  },

  getSession: async () => {
    return await apiCall("/auth/session", {
      credentials: "include",
    });
  },

  logout: async () => {
    return await apiCall("/auth/logout", {
      method: "POST",
      credentials: "include",
    });
  },

  /** Sign in with Apple: verify the identity token server-side, set the session
   *  cookie (web/WebView) and return the bearer token (native). */
  appleAuth: async (identityToken, nonce = null, user = null) => {
    return await apiCall("/auth/apple", {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({ identity_token: identityToken, nonce, user }),
    });
  },

  /** Permanently delete the signed-in user's account (Google Sign-In compliance). */
  deleteAccount: async () => {
    return await apiCall("/auth/account", {
      method: "DELETE",
      credentials: "include",
    });
  },

  getProfilePresence: async ({ userId, token = null }) => {
    const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : "";
    return await apiCall(`/user/profile${qs}`, {
      headers: buildIdentityHeaders({ token, userId }),
    });
  },

  getMe: async ({ userId, token = null }) => {
    const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : "";
    return await apiCall(`/users/me${qs}`, {
      headers: buildIdentityHeaders({ token, userId }),
    });
  },

  getUnifiedMe: async ({ userId, token = null }) => {
    const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : "";
    return await apiCall(`/users/me/unified${qs}`, {
      headers: buildIdentityHeaders({ token, userId }),
    });
  },

  updateMe: async ({ userId, token = null, profile = {} }) => {
    const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : "";
    return await apiCall(`/users/me${qs}`, {
      method: "PUT",
      body: JSON.stringify(profile),
      headers: buildIdentityHeaders({ token, userId }),
    });
  },
};
